import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import config from '../config/api';
import {
  HomeIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  ServerIcon,
  UserIcon,
  LifebuoyIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CreditCardIcon,
  CodeBracketIcon,
  ChevronRightIcon,
  BellIcon,
  Cog6ToothIcon,
  SparklesIcon,
  CpuChipIcon,
  SignalIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [wallet, setWallet] = useState(null);

  // Fetch wallet balance
  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(config.WALLET_API_URL, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWallet(data);
        }
      } catch (err) {
        console.error('Error fetching wallet:', err);
      }
    };
    fetchWallet();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  const menuItems = [
    { name: 'Trang chủ', href: '/dashboard', icon: HomeIcon },
    { name: 'Dự án', href: '/projects', icon: ClipboardDocumentListIcon },
    { name: 'Hợp đồng', href: '/contracts', icon: DocumentTextIcon },
    { name: 'Hosting & Domain', href: '/hosting', icon: ServerIcon },
    { name: 'Landing Pages', href: '/landing-pages', icon: DocumentDuplicateIcon },
    { name: 'Ví điện tử', href: '/wallet', icon: CreditCardIcon },
    { name: 'Hỗ trợ', href: '/support', icon: LifebuoyIcon },
  ];

  const bottomMenuItems = [
    { name: 'Hồ sơ', href: '/profile', icon: UserIcon },
    { name: 'API & JSON', href: '/customer-profile', icon: CodeBracketIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => location.pathname === href;

  const getPageTitle = () => {
    const allItems = [...menuItems, ...bottomMenuItems];
    const currentItem = allItems.find(item => isActive(item.href));
    return currentItem?.name || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-dark-900">
      <div className="flex h-screen">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
          </div>
        )}

        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-50 w-72 bg-dark-900 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 border-r border-cyan-500/10 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Sidebar header */}
            <div className="flex items-center justify-between h-20 px-6 border-b border-cyan-500/10 bg-dark-800/50">
              <div className="flex items-center gap-3">
                <Logo size="md" />
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-xl hover:bg-cyan-500/10 transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-cyan-400" />
              </button>
            </div>

            {/* User info */}
            <div className="px-4 py-5">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 rounded-2xl border border-cyan-500/20">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-glow-cyan">
                  {user?.company_name?.[0] || user?.email?.[0] || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{user?.company_name || 'Customer'}</p>
                  <p className="text-xs text-cyan-400/70 truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-2 overflow-y-auto">
              <p className="px-4 text-xs font-semibold text-cyan-400/50 uppercase tracking-wider mb-3 font-mono">Menu chính</p>
              <div className="space-y-1">
                {menuItems.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <button
                      key={item.name}
                      onClick={() => {
                        navigate(item.href);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                        active
                          ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/10 text-cyan-400 border-l-2 border-cyan-400 shadow-glow-cyan/20'
                          : 'text-ocean-100/60 hover:bg-cyan-500/10 hover:text-cyan-400'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${active ? 'text-cyan-400' : 'text-ocean-100/40 group-hover:text-cyan-400'}`} />
                      <span className="font-medium">{item.name}</span>
                      {active && <ChevronRightIcon className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-cyan-500/10"></div>

              {/* Bottom menu items */}
              <p className="px-4 text-xs font-semibold text-cyan-400/50 uppercase tracking-wider mb-3 font-mono">Tài khoản</p>
              <div className="space-y-1">
                {bottomMenuItems.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <button
                      key={item.name}
                      onClick={() => {
                        navigate(item.href);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                        active
                          ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/10 text-cyan-400 border-l-2 border-cyan-400'
                          : 'text-ocean-100/60 hover:bg-cyan-500/10 hover:text-cyan-400'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${active ? 'text-cyan-400' : 'text-ocean-100/40 group-hover:text-cyan-400'}`} />
                      <span className="font-medium">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Logout button */}
            <div className="p-4 border-t border-cyan-500/10">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all duration-200 group"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span className="font-medium">Đăng xuất</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top header */}
          <header className="bg-dark-800/80 backdrop-blur-xl border-b border-cyan-500/10 flex-shrink-0 sticky top-0 z-30">
            <div className="flex items-center justify-between h-20 px-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2.5 rounded-xl hover:bg-cyan-500/10 transition-colors"
                >
                  <Bars3Icon className="w-6 h-6 text-cyan-400" />
                </button>
                <div className="hidden md:block">
                  <div className="flex items-center gap-2">
                    <CpuChipIcon className="w-5 h-5 text-cyan-400" />
                    <h1 className="text-xl font-bold text-white">{getPageTitle()}</h1>
                  </div>
                  <p className="text-sm text-ocean-100/50">Quản lý và theo dõi các dịch vụ của bạn</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Wallet Balance */}
                <button
                  onClick={() => navigate('/wallet')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 rounded-xl hover:from-cyan-500/30 hover:to-blue-500/30 transition-all shadow-glow-cyan/20 hover:shadow-glow-cyan"
                >
                  <CreditCardIcon className="w-5 h-5" />
                  <span className="font-bold font-mono">{wallet ? formatCurrency(wallet.balance) : '---'}</span>
                  <span className="text-cyan-300 text-sm">₫</span>
                </button>

                {/* Notifications */}
                <button className="relative p-2.5 rounded-xl hover:bg-cyan-500/10 transition-colors group">
                  <BellIcon className="w-6 h-6 text-ocean-100/50 group-hover:text-cyan-400" />
                  <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-cyan-400 rounded-full border-2 border-dark-800 animate-pulse shadow-glow-cyan"></span>
                </button>

                {/* Settings */}
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2.5 rounded-xl hover:bg-cyan-500/10 transition-colors group"
                >
                  <Cog6ToothIcon className="w-6 h-6 text-ocean-100/50 group-hover:text-cyan-400" />
                </button>

                {/* User menu */}
                <div className="hidden sm:flex items-center gap-3 pl-4 border-l border-cyan-500/10">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-white">
                      {user?.company_name || 'Customer'}
                    </div>
                    <div className="text-xs text-cyan-400/70">
                      {user?.email}
                    </div>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-glow-cyan">
                    {user?.company_name?.[0] || user?.email?.[0] || 'U'}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-dark-800/50 border-t border-cyan-500/10 py-4 px-6">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-ocean-100/50">
              <div className="flex items-center gap-2">
                <SignalIcon className="w-4 h-4 text-cyan-400" />
                <p>© 2024 <span className="font-semibold text-cyan-400">Smartera</span>. Tất cả quyền được bảo lưu.</p>
              </div>
              <div className="flex items-center gap-6">
                <a href="/terms" className="hover:text-cyan-400 transition-colors">Điều khoản</a>
                <a href="/privacy" className="hover:text-cyan-400 transition-colors">Bảo mật</a>
                <a href="/support" className="hover:text-cyan-400 transition-colors">Hỗ trợ</a>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Layout;
